import * as Api from '@ViewModels';
import { Industry, ISocialMediaPost } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { ICreateSocialMediaPostRequest, ILocationState } from '../../../../models';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { getPlainTextFromPreviewHtml, isTemplateNew } from '../../../../models/UiUtils';
import { ITemplateCard, TemplateScope } from '../../../../viewmodels/AppViewModels';
import { Button } from '../../../components-v2/Button';
import { ButtonGroup } from '../../../components-v2/ButtonGroup';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../components-v2/Dropdown';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { ButtonBase } from '../../Button';
import { CampaignExpiryHint } from '../../campaigns/CampaignExpiryHint';
import '../../richContent/RichContentDocumentEditor/placeholders.tinymce.css';
import {
	BasicImagePreview,
	BasicVideoPreview,
	InstagramImagePreview,
	PreviewSize,
} from '../../socialMedia/PreviewSocialMediaPost/presentation';
import { ChevronDown } from '../../svgs/icons/ChevronDown';
import { ImageMissingIcon } from '../../svgs/icons/ImageMissingIcon';
import { SocialMediaIndicatorIcon } from '../../svgs/icons/SocialMediaIndicatorIcon';
import { hasCccKeyword } from '../../../containers/Campaigns/utils';
import { hasTemplateReviewCapability } from '../../TemplateReviewStatusIndicator';
import { PopoverType, TinyPopover } from '../../TinyPopover';
import { TemplateDateFields } from '../TemplateDateFields';
import { TemplateKeywords } from '../TemplateKeywords';
import { SocialCampaignTemplateCardSummary } from './presentation';
import { styleSheet } from './styles';
import { TemplateUserGroups } from '../TemplateUserGroups';

interface Props {
	card: ITemplateCard | ISocialMediaPost;
	className?: string;
	isFeatured?: boolean;
	onPreviewClick(): void;
	onAssociatedTemplateClicked?(e: React.MouseEvent<Element>): void;
	styles?: StyleDeclarationValue[];
	contextMenu?: React.ReactNode;
	industry?: Industry;
	invalidateTemplates?: () => void;
	userGroups?: Api.IUserGroup[];
}

export function SocialCampaignTemplateCard(props: Props) {
	const { card, className, onPreviewClick, onAssociatedTemplateClicked, styles, userGroups } = props;
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const [showAssociatedPopover, , setShowAssociatedPopover] = useLambda(false);

	const onAssociatedIndicatorClicked = (e: React.MouseEvent<SVGSVGElement>) => {
		setShowAssociatedPopover(false)();
		onAssociatedTemplateClicked?.(e);
	};

	const tCard = card as ITemplateCard;
	const expirationDate = tCard?.schedule?.expirationDate ? moment(tCard?.schedule?.expirationDate) : undefined;
	const hasAssociatedTemplateOrCampaign = tCard.associatedTemplates?.length > 0;

	// Keywords logic - allow any user to add keywords for organizing templates into folders
	// Limited users can only edit keywords on templates they created
	const isLimitedUser = userSession.userRole === 'limitedUser';
	const isCreator = tCard.creator?.id === userSession.user.id;
	const isSystemTemplate = tCard.scope === TemplateScope.System;
	const canEditKeywords = !isSystemTemplate && (isLimitedUser ? isCreator : true);
	const hasKeywords = tCard?.keywords && tCard.keywords.length > 0;

	// Show keywords if user can edit them
	const showKeywords =
		!isSystemTemplate && props.industry && props.invalidateTemplates && tCard.id && (canEditKeywords || hasKeywords);
	const hasCcc = hasCccKeyword(tCard);
	const showDateFields = !isSystemTemplate && props.invalidateTemplates && tCard.id && hasCcc;
	const canReviewTemplates = hasTemplateReviewCapability(userSession);
	const handleReviewClick = async () => {
		const templates = new Api.TemplatesViewModel(userSession);
		const fetchedTemplate = await templates.getByIdExpandedByLastUsedBy(card.id);
		const locationState: ILocationState<any, ICreateSocialMediaPostRequest<Api.ITemplate>> = {
			model: {
				context: { ...fetchedTemplate },
				type: 'Template',
			},
		};
		fullscreenModal?.history.push({
			pathname: '/social-media/post/review/from-template',
			state: locationState,
		});
	};
	return (
		<div
			className={`${css(styleSheet.container, styleSheet.containerPost, ...(styles || []))}
			social-campaign-template-card
			${className || ''}`}
		>
			<div className={css(bs.flex)}>
				<div
					className={`campaign-template-card-name ${css(
						!hasAssociatedTemplateOrCampaign && styleSheet.name,
						styleSheet.postName,
						baseStyleSheet.truncateText
					)}`}
				>
					<div
						className={css(
							bs.bgBackground,
							bs.flex,
							bs.gap2,
							bs.itemsCenter,
							bs.justifyBetween,
							bs.boxBorder,
							bs.border0,
							bs.borderB,
							bs.borderSolid,
							bs.borderNameCircles,
							bs.p4,
							bs.wFull
						)}
					>
						<div className={css(bs.truncateText, bs.flex, bs.itemsCenter, bs.gap2)}>
							<ButtonBase className={css(bs.truncateText, bs.fontBold, bs.textTitles)} onClick={onPreviewClick}>
								{card.name || 'Custom Post'}
							</ButtonBase>
							{expirationDate ? <CampaignExpiryHint expirationDate={expirationDate} /> : null}
							{isTemplateNew(tCard) ? <span className={css(styleSheet.newItem)}>(New!)</span> : null}
						</div>
						{props.contextMenu ?? null}
					</div>
				</div>
				{hasAssociatedTemplateOrCampaign && (
					<TinyPopover
						align='end'
						anchor={
							<SocialMediaIndicatorIcon
								className={css(
									bs.absolute,
									bs.top0,
									bs.right0,
									bs.cursorPointer,
									showAssociatedPopover && styleSheet.flagHighlight
								)}
								fillColor='#89C947'
								onClick={onAssociatedIndicatorClicked}
								onMouseEnter={setShowAssociatedPopover(true)}
								onMouseLeave={setShowAssociatedPopover(false)}
							/>
						}
						dismissOnOutsideAction={true}
						isOpen={showAssociatedPopover}
						placement={['top', 'left']}
						type={PopoverType.white}
					>
						<div className={css(styleSheet.popover)}>Also available as an email campaign.</div>
					</TinyPopover>
				)}
			</div>
			<div className={css(styleSheet.summaryPadding)}>
				<SocialCampaignTemplateCardSummary
					content={card?.content}
					ctaClicked={onPreviewClick}
					summary={getPlainTextFromPreviewHtml(tCard?.previewHtml) || tCard?.summary}
					bigScreenCutoff={120}
				/>
			</div>
			{card?.attachments?.length > 0 ? (
				card?.attachments?.[0].mimeType?.startsWith('video/') ? (
					<BasicVideoPreview
						previewSize={PreviewSize.Sm}
						postImages={card?.attachments}
						onCtaClick={onPreviewClick}
						controls={false}
					/>
				) : card?.attachments?.length > 1 ? (
					<InstagramImagePreview
						previewSize={PreviewSize.Sm}
						postImages={card?.attachments}
						onCtaClick={onPreviewClick}
					/>
				) : (
					<BasicImagePreview previewSize={PreviewSize.Sm} postImages={card?.attachments} onCtaClick={onPreviewClick} />
				)
			) : (
				<p className={css(styleSheet.imageContainer)}>
					<ImageMissingIcon />
				</p>
			)}
			<div className={css(styleSheet.buttonStack)}>
				<ButtonGroup styles={[bs.wFull]}>
					{!canReviewTemplates ? (
						<Button variant='secondary' size='sm' onClick={() => onPreviewClick()} styles={[bs.flex1]}>
							Preview
						</Button>
					) : null}
					{canReviewTemplates ? (
						<>
							<Button variant='secondary' size='sm' onClick={handleReviewClick} styles={[bs.flex1]}>
								Review
							</Button>
							<Dropdown>
								<DropdownTrigger render={<Button variant='secondary' size='sm' />}>
									<ChevronDown />
								</DropdownTrigger>
								<DropdownContent align='end'>
									<DropdownItem onClick={() => onPreviewClick()}>Preview</DropdownItem>
								</DropdownContent>
							</Dropdown>
						</>
					) : null}
				</ButtonGroup>
				{showKeywords ? (
					<TemplateKeywords
						keywords={tCard.keywords || []}
						templateId={tCard.id}
						industry={props.industry}
						invalidateTemplates={props.invalidateTemplates}
						readOnly={!canEditKeywords}
						templateTypes={[Api.TemplateType.SocialMediaPost]}
					/>
				) : null}
				{showDateFields ? (
					<TemplateDateFields
						template={tCard}
						templateType={Api.TemplateType.SocialMediaPost}
						onSuccess={props.invalidateTemplates}
					/>
				) : null}
				{<TemplateUserGroups card={card as ITemplateCard} userGroups={userGroups} />}
				{tCard.scope === TemplateScope.Account && tCard.creator?.id !== userSession.user?.id
					? (() => {
							let sharedByName = 'UNKNOWN';
							if (tCard.creator?.firstName || tCard.creator?.lastName) {
								sharedByName = `${tCard.creator?.firstName} ${tCard.creator?.lastName}`;
							}
							return <div className={css(styleSheet.sharedByFooter)}>Shared by {sharedByName}</div>;
						})()
					: null}
			</div>
		</div>
	);
}
